import React, { useState, useEffect } from "react";
import { apiService } from "../../services/api";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { PhotoIcon } from "@heroicons/react/24/solid";
import CompanyUsersTab from "./tabs/CompanyUsersTab";
import CompanyAlertsTab from "./tabs/CompanyAlertsTab";
import CompanyBranchesTab from "./tabs/CompanyBranchesTab";
import { ProhibitedSitesTab } from "../../components/company/ProhibitedSitesTab";

export default function CompanySettingsPage() {
  const [activeTab, setActiveTab] = useState<
    "general" | "users" | "alerts" | "branches" | "prohibited-sites"
  >("general");
  const [settings, setSettings] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      setError(""); // Clear previous errors
      const response = await apiService.getCompanySettings();
      if (response && response.success && response.data) {
        setSettings(response.data);
      } else {
        setError("Erro ao carregar configurações da empresa: resposta inválida");
      }
    } catch (err: any) {
      console.error("Error loading company settings:", err);
      setError(err.response?.data?.message || "Erro ao carregar configurações da empresa");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      setError("");
      setSuccessMessage("");

      await apiService.updateCompanySettings({
        name: settings.name,
        admin_phone: settings.admin_phone,
        admin_email: settings.admin_email,
        address_street: settings.address_street,
        address_number: settings.address_number,
        address_city: settings.address_city,
        address_state: settings.address_state,
        address_zip: settings.address_zip,
      });

      setSuccessMessage("Configurações salvas com sucesso!");
    } catch (err) {
      setError("Erro ao salvar configurações");
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "logo" | "background",
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsSaving(true);
      setError("");

      let response;
      if (type === "logo") {
        response = await apiService.uploadCompanyLogo(file);
        setSettings({ ...settings, logo_url: response.data.logo_url });
      } else {
        response = await apiService.uploadCompanyBackground(file);
        // Add timestamp to prevent browser cache
        const timestamp = new Date().getTime();
        const backgroundUrlWithCache = response.data.background_url.includes('?')
          ? `${response.data.background_url}&t=${timestamp}`
          : `${response.data.background_url}?t=${timestamp}`;
        setSettings({
          ...settings,
          background_url: backgroundUrlWithCache,
        });
      }

      setSuccessMessage(
        `${type === "logo" ? "Logo" : "Fundo"} atualizado com sucesso!`,
      );
    } catch (err) {
      setError(`Erro ao fazer upload do ${type === "logo" ? "logo" : "fundo"}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveBackground = async () => {
    if (!window.confirm("Tem certeza que deseja remover a imagem de fundo?"))
      return;

    try {
      setIsSaving(true);
      setError("");

      await apiService.deleteCompanyBackground();
      setSettings({ ...settings, background_url: null });

      setSuccessMessage("Imagem de fundo removida com sucesso!");
    } catch (err) {
      setError("Erro ao remover imagem de fundo");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
        Configurações da Empresa
      </h1>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("general")}
            className={`${
              activeTab === "general"
                ? "border-blue-500 text-blue-600 dark:text-blue-400"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Geral
          </button>
          <button
            onClick={() => setActiveTab("users")}
            className={`${
              activeTab === "users"
                ? "border-blue-500 text-blue-600 dark:text-blue-400"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Usuários
          </button>
          <button
            onClick={() => setActiveTab("alerts")}
            className={`${
              activeTab === "alerts"
                ? "border-blue-500 text-blue-600 dark:text-blue-400"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Regras de Alerta
          </button>
          <button
            onClick={() => setActiveTab("branches")}
            className={`${
              activeTab === "branches"
                ? "border-blue-500 text-blue-600 dark:text-blue-400"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Filiais
          </button>
          <button
            onClick={() => setActiveTab("prohibited-sites")}
            className={`${
              activeTab === "prohibited-sites"
                ? "border-blue-500 text-blue-600 dark:text-blue-400"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Sites Proibidos
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "general" && (
        <>
          {error && (
            <div
              className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative"
              role="alert"
            >
              <span className="block sm:inline">{error}</span>
            </div>
          )}

          {successMessage && (
            <div
              className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative"
              role="alert"
            >
              <span className="block sm:inline">{successMessage}</span>
            </div>
          )}

          <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                  {/* Company Name */}
                  <div className="sm:col-span-4">
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Nome da Empresa
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="name"
                        id="name"
                        value={settings?.name || ""}
                        onChange={(e) =>
                          setSettings({ ...settings, name: e.target.value })
                        }
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full text-base py-3 border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  </div>

                  {/* Logo Upload */}
                  <div className="sm:col-span-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Logo
                    </label>
                    <div className="mt-1 flex items-center">
                      <span className="h-24 w-24 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center border border-gray-200 dark:border-gray-600">
                        {settings?.logo_url ? (
                          <img
                            src={settings.logo_url}
                            alt="Logo"
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <PhotoIcon className="h-12 w-12 text-gray-300" />
                        )}
                      </span>
                      <label
                        htmlFor="logo-upload"
                        className="ml-5 bg-white dark:bg-gray-700 py-2 px-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
                      >
                        <span>Alterar Logo</span>
                        <input
                          id="logo-upload"
                          name="logo-upload"
                          type="file"
                          className="sr-only"
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, "logo")}
                        />
                      </label>
                    </div>
                  </div>

                  {/* Background Upload */}
                  <div className="sm:col-span-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Imagem de Fundo (Background)
                    </label>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                      Esta imagem será exibida como fundo em todas as telas da
                      aplicação para os usuários desta empresa.
                    </p>
                    <div className="mt-1 flex items-center">
                      <span className="h-32 w-56 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center border border-gray-200 dark:border-gray-600">
                        {settings?.background_url ? (
                          <img
                            src={settings.background_url}
                            alt="Background"
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <PhotoIcon className="h-12 w-12 text-gray-300" />
                        )}
                      </span>
                      <label
                        htmlFor="background-upload"
                        className="ml-5 bg-white dark:bg-gray-700 py-2 px-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
                      >
                        <span>Alterar Fundo</span>
                        <input
                          id="background-upload"
                          name="background-upload"
                          type="file"
                          className="sr-only"
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, "background")}
                        />
                      </label>
                      {settings?.background_url && (
                        <button
                          type="button"
                          onClick={handleRemoveBackground}
                          className="ml-3 bg-white dark:bg-gray-700 py-2 px-3 border border-red-300 dark:border-red-600 rounded-md shadow-sm text-sm leading-4 font-medium text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          Remover Fundo
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="sm:col-span-3">
                    <label
                      htmlFor="admin_email"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Email Administrativo
                    </label>
                    <div className="mt-1">
                      <input
                        type="email"
                        name="admin_email"
                        id="admin_email"
                        value={settings?.admin_email || ""}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            admin_email: e.target.value,
                          })
                        }
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full text-base py-3 border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-3">
                    <label
                      htmlFor="admin_phone"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Telefone
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="admin_phone"
                        id="admin_phone"
                        value={settings?.admin_phone || ""}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            admin_phone: e.target.value,
                          })
                        }
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full text-base py-3 border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  </div>

                  {/* Address */}
                  <div className="sm:col-span-6">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mt-4 mb-2">
                      Endereço
                    </h3>
                  </div>

                  <div className="sm:col-span-4">
                    <label
                      htmlFor="address_street"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Rua
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="address_street"
                        id="address_street"
                        value={settings?.address_street || ""}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            address_street: e.target.value,
                          })
                        }
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full text-base py-3 border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label
                      htmlFor="address_number"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Número
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="address_number"
                        id="address_number"
                        value={settings?.address_number || ""}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            address_number: e.target.value,
                          })
                        }
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full text-base py-3 border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label
                      htmlFor="address_city"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Cidade
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="address_city"
                        id="address_city"
                        value={settings?.address_city || ""}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            address_city: e.target.value,
                          })
                        }
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full text-base py-3 border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label
                      htmlFor="address_state"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Estado
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="address_state"
                        id="address_state"
                        value={settings?.address_state || ""}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            address_state: e.target.value,
                          })
                        }
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full text-base py-3 border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label
                      htmlFor="address_zip"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      CEP
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="address_zip"
                        id="address_zip"
                        value={settings?.address_zip || ""}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            address_zip: e.target.value,
                          })
                        }
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full text-base py-3 border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Contract Section */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Contrato
                  </h3>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md p-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        Contrato de Prestação de Serviços
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {settings?.contract_expiration_date
                          ? `Válido até ${new Date(
                              settings.contract_expiration_date,
                            ).toLocaleDateString()}`
                          : "Data de expiração não disponível"}
                      </p>
                    </div>
                    {settings?.contract_url ? (
                      <a
                        href={settings.contract_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700"
                      >
                        <svg
                          className="-ml-1 mr-2 h-5 w-5 text-gray-500 dark:text-gray-400"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        Visualizar Contrato
                      </a>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                        Contrato não disponível
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {isSaving ? "Salvando..." : "Salvar Alterações"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {activeTab === "users" && <CompanyUsersTab />}
      {activeTab === "alerts" && <CompanyAlertsTab />}
      {activeTab === "branches" && <CompanyBranchesTab />}
      {activeTab === "prohibited-sites" && <ProhibitedSitesTab />}
    </div>
  );
}
